import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { LuLock } from 'react-icons/lu';
import { formatMoney } from '../../utils/formatters';
import RateSourceTooltip from './RateSourceTooltip';

/**
 * Single-line movement row for widgets and side panels.
 * Features are opt-in via props so each caller renders only what its context needs.
 *
 * Layout:
 *   [leading] [note] [accountName?] [tags?] [category?] [amount]
 *
 * `subNote` is a small second line under the note: a trade's "2 SPY @ 150.00".
 *
 * `size` "md" is the detail panels' row, Copilot's: a 14px name, and the
 * amount in semibold with its currency code small and raised.
 *
 * `approx` marks an amount converted to the display currency (`≈`, and the
 * rates it read in `rateSources` on hover); `pending` dims one whose converted
 * value has not arrived yet.
 *
 * When `accountName` is undefined the note expands to fill the remaining space;
 * when it's provided (including empty string) the note stays at a fixed width and
 * the account column takes the remaining space.
 */
export default function CompactMovementRow({
  leading,
  note,
  subNote,
  noteRestricted = false,
  struck = false,
  restrictedLabel = 'Restricted',
  restrictedTooltip,
  accountName,
  tags,
  category,
  amount,
  currency,
  refund = false,
  onClick,
  size = 'sm',
  approx = false,
  pending = false,
  rateSources,
}) {
  const md = size === 'md';
  const isClickable = typeof onClick === 'function';
  const showAccount = accountName !== undefined;

  const noteContent = noteRestricted ? (
    <Flex align="center" gap={1} minW={0}>
      <Tooltip label={restrictedTooltip} fontSize="xs" hasArrow isDisabled={!restrictedTooltip}>
        <Box as="span" color="redesign.text.muted" display="inline-flex" flexShrink={0}>
          <LuLock size={11} />
        </Box>
      </Tooltip>
      <Text fontSize={md ? '14px' : 'xs'} fontWeight="500" color="redesign.text.muted" fontStyle="italic" isTruncated>
        {restrictedLabel}
      </Text>
    </Flex>
  ) : (
    <Text fontSize={md ? '14px' : 'xs'} fontWeight="500" color="redesign.heading" isTruncated textDecoration={struck ? 'line-through' : 'none'}>{note || '—'}</Text>
  );
  const noteBlock = subNote ? (
    <Box minW={0}>
      {noteContent}
      <Text fontSize="2xs" color="redesign.text.muted" isTruncated>{subNote}</Text>
    </Box>
  ) : noteContent;

  return (
    <Flex
      role="group"
      align="center"
      gap={{ base: 1, md: 2 }}
      py="2px"
      px={1}
      mx={-1}
      borderRadius="md"
      cursor={isClickable ? 'pointer' : 'default'}
      _hover={isClickable ? { bg: 'redesign.bg.hover' } : undefined}
      onClick={onClick}
    >
      {leading != null && <Box flexShrink={0}>{leading}</Box>}

      {showAccount ? (
        <>
          <Box flexShrink={1} minW={0} maxW={{ base: '90px', md: '140px' }}>
            {noteBlock}
          </Box>
          {typeof accountName === 'string' ? (
            <Text fontSize="xs" color="redesign.text.muted" isTruncated flex={1} minW={0} title={accountName || undefined}>
              {accountName}
            </Text>
          ) : (
            <Box fontSize="xs" color="redesign.text.muted" flex={1} minW={0} overflow="hidden">
              {accountName}
            </Box>
          )}
        </>
      ) : (
        // A floor for the note, wide enough for a usual name ("Netflix
        // subscription"), so a long category pill gives way (it shrinks first
        // and truncates) instead of squeezing the name to a letter.
        <Box flex={1} minW={{ base: 0, md: '120px' }}>
          {noteBlock}
        </Box>
      )}

      {tags != null && (
        // The panels' row lets an empty slot (the 14px "add tag" icon) take
        // only its own width: the name needs the room more than the column.
        <Flex minW={md ? undefined : { base: '30px', md: '45px' }} justify="flex-end" flexShrink={0} onClick={(e) => e.stopPropagation()}>{tags}</Flex>
      )}
      {category != null && (
        // The chip gives way before the name does. With equal shrink the two
        // squeezed together and a narrowed panel rendered no name at all; a
        // floor on the name instead padded short names and pushed the account
        // away from them. Weighting the shrink leaves names at their natural
        // width and takes the shortfall from the chip, whose label truncates —
        // down to a width that still shows the start of it. Below that a chip is
        // a grey sliver that reads as broken, so the name gives the rest.
        <Box flexShrink={1000} minW={{ base: '40px', md: '48px' }} maxW={{ base: '90px', md: 'none' }} overflow="hidden" onClick={(e) => e.stopPropagation()}>{category}</Box>
      )}

      <RateSourceTooltip sources={rateSources} isDisabled={!approx}>
      <Text
        fontSize={md ? '14px' : 'xs'}
        fontWeight={md ? 600 : 500}
        color={refund ? 'redesign.amount.income' : 'redesign.heading'}
        flexShrink={0}
        // Measured at this font: COP millions need 102px and tens of millions
        // 113px. At 80px they wrapped onto a second line ("COP / 3,316,204.30"),
        // and 110px was already short for a credit card balance. The "md" row
        // needs 122px for tens of millions, and grows past it for a `≈`.
        w={md ? undefined : { base: '104px', md: '118px' }}
        minW={md ? '128px' : undefined}
        whiteSpace="nowrap"
        textAlign="right"
        opacity={pending ? 0.35 : 1}
        transition="opacity 0.15s ease-out"
      >
        {approx && <Text as="span" mr="3px">≈</Text>}
        {md ? (
          <>
            <Text as="span" fontSize="10px" verticalAlign="0.35em" mr="3px" color={refund ? undefined : 'redesign.text.muted'}>{currency}</Text>
            {formatMoney(Math.abs(amount), currency, true)}
          </>
        ) : formatMoney(Math.abs(amount), currency)}
      </Text>
      </RateSourceTooltip>
    </Flex>
  );
}
